import {
  BellFilled,
  CalendarFilled,
  CheckCircleFilled,
  EditOutlined,
  FrownFilled,
  MehFilled,
  PlusOutlined,
  SmileFilled,
} from '@ant-design/icons';
import Drop from 'assets/drop.svg?react';
import ModuleCard from 'components/cards/ModuleCard';
import SymptomTag from 'components/SymptomTag';
import { Button } from 'components/ui';
import dayjs from 'dayjs';
import { colors, symptomSeverity } from 'design';
import useShouldAskForPeriod from 'hooks/useShouldAskForPeriod';
import useSymptomTrackingData from 'hooks/useSymptomTrackingData';
import SymptomTrackingSurveyModal from 'pages/SymptomTrackingSurvey';
import posthog from 'posthog-js';
import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { createUseStyles } from 'react-jss';
import { DailyStatus } from 'services/api/models/SymptomTrackingDTO';
import SymptomTrackingReminderModal from './modals/SymptomTrackingReminderModal';

const useStyles = createUseStyles({
  // Container styles
  mainContainer: {
    position: 'relative',
    width: '100%',
  },
  scrollContainer: {
    width: '100%',
    overflowX: 'auto',
    scrollSnapType: 'x mandatory',
    display: 'flex',
    alignItems: 'stretch',
    gap: 16,
    paddingLeft: '18%', // Add padding to center the cards while showing adjacent ones
    paddingRight: '18%',
    height: '100%',
    // Center on middle card initially
    scrollBehavior: 'smooth',
    scrollbarWidth: 'none',
    paddingBottom: 16,
  },
  cardWrapper: {
    minWidth: '100%',
    scrollSnapAlign: 'center',
  },
  cardWrapperFilledOut: {
    composes: '$cardWrapper',
    position: 'relative',
    '& .ant-card': {
      position: 'relative',
      zIndex: 1,
      height: '100%',
    },
    '& .ant-card-body': {
      height: '100%',
      display: 'flex',
      flexDirection: 'column',
    },
  },
  calendarCardWrapper: {
    minWidth: 'fit-content',
    scrollSnapAlign: 'center',
    height: '100%',
    display: 'flex',
    alignItems: 'center',
    paddingRight: 16,
    paddingLeft: 16,
    justifyContent: 'center',
    '& .ant-card': {
      width: 'fit-content',
      height: 'fit-content',
      background: 'transparent',
      boxShadow: 'none',
    },
    '& .ant-card-body': {
      padding: 0,
      height: 'auto',
      width: '100%',
      background: 'transparent',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
    },
  },

  // Fade overlay styles
  fadeOverlayLeft: {
    position: 'absolute',
    left: 0,
    top: 0,
    bottom: 0,
    width: '5%',
    background: `linear-gradient(to right, ${colors.surface.page} 0%, transparent 100%)`,
    zIndex: 10,
    pointerEvents: 'none',
  },
  fadeOverlayRight: {
    position: 'absolute',
    right: 0,
    top: 0,
    bottom: 0,
    width: '5%',
    background: `linear-gradient(to left, ${colors.surface.page} 0%, transparent 100%)`,
    zIndex: 10,
    pointerEvents: 'none',
  },

  // Card content styles
  cardContent: {
    paddingTop: 16,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    width: '100%',
    flex: 1,
    overflow: 'hidden',
  },

  infoRow: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    width: '100%',
    marginBottom: 8,
  },

  // Tracking data display styles
  infoContainer: {
    display: 'flex',
    flexDirection: 'column',
    marginBottom: 8,
    width: '100%',
    '& h4': {
      color: colors.text.tertiary,
      fontSize: 14,
      fontWeight: 'normal',
      marginBottom: 0,
    },
  },
  infoLabel: {
    fontWeight: 'bold',
  },
  dropIconsContainer: {
    display: 'flex',
    alignItems: 'center',
    marginLeft: -2,
    marginBottom: 4,
  },
  periodLabel: {
    margin: 0,
    fontWeight: 'bold',
    fontSize: 16,
  },
  symptomsSection: {
    marginBottom: 12,
    position: 'relative',
  },
  symptomsContainer: {
    marginTop: 4,
    display: 'flex',
    gap: 4,
    justifyContent: 'flex-start',
    overflowX: 'auto',
    flexWrap: 'nowrap',
    maxWidth: '100%',
    width: '100%',
    '&::-webkit-scrollbar': {
      height: 0,
      display: 'none',
    },
  },
  symptomsFadeOverlay: {
    position: 'absolute',
    right: 0,
    top: 24, // Position below the "Symptômes" label
    bottom: 0,
    width: 20,
    background: 'linear-gradient(to left, white 0%, transparent 100%)',
    pointerEvents: 'none',
    zIndex: 1,
  },
  noSymptomsText: {
    margin: 0,
    fontWeight: 'bold',
    fontSize: 16,
  },

  // Action button styles
  editIcon: {
    fontSize: 24,
    color: colors.brand.primary,
  },
  addIcon: {
    fontSize: 24,
    color: colors.brand.onPrimary,
  },

  // Tomorrow card styles
  reminderEmoji: {
    fontSize: 32,
    color: colors.border.default,
  },
  reminderTitle: {
    margin: 0,
    fontWeight: 'bold',
  },
  reminderSubtitle: {
    margin: 0,
    fontSize: 14,
    color: colors.text.tertiary,
  },
  calendarIcon: {
    fontSize: 26,
    color: colors.brand.accentStrong,
    marginBottom: 8,
  },
  calendarCardContent: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    height: 'auto',
    cursor: 'pointer',
    padding: 16,
    borderRadius: 16,
  },

  dayCardTitle: {
    textTransform: 'capitalize',
  },
});

// Number of past days (before yesterday) displayed as their own cards, in addition to
// the "Yesterday" and "Today" cards, so that there are 7 day-tracking cards in total.
const NUMBER_OF_ADDITIONAL_PAST_DAYS = 5;

const useActionButtonStyles = createUseStyles({
  actionButton: (props: { hasTracking: boolean }) => ({
    width: 48,
    height: 48,
    marginTop: 12,
    borderRadius: '50%',
    backgroundColor: props.hasTracking ? 'rgba(255, 255, 255, 0)' : colors.brand.primary,
    border: props.hasTracking ? `2px solid ${colors.brand.primary}` : 'none',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
  }),
});

interface TrackingDataDisplayProps {
  dayData: DailyStatus | undefined;
  onEditClick: () => void;
  posthogSource?: string;
  testIdLabel: string;
}

const TrackingDataDisplay: React.FC<TrackingDataDisplayProps> = ({
  dayData,
  onEditClick,
  posthogSource = 'horizontal_cards',
  testIdLabel,
}) => {
  const { t } = useTranslation();
  const styles = useStyles();
  const actionButtonStyles = useActionButtonStyles({ hasTracking: dayData !== undefined });

  const { isPeriodTrackingEnabled } = useShouldAskForPeriod();

  // Function to render general state icon with proper color and text
  const renderGeneralStateIcon = () => {
    if (dayData === undefined || dayData.general_state === null || dayData.general_state === undefined) {
      return (
        <div style={{ display: 'flex', alignItems: 'center', gap: 4, color: colors.border.default }}>
          <SmileFilled style={{ color: colors.border.default }} />
          <span>-</span>
        </div>
      );
    }

    const value = dayData.general_state;
    let icon;
    let key: keyof typeof symptomSeverity;

    if (value === 4) {
      icon = <SmileFilled />;
      key = 'veryGood';
    } else if (value === 3) {
      icon = <SmileFilled />;
      key = 'good';
    } else if (value === 2) {
      icon = <MehFilled />;
      key = 'average';
    } else if (value === 1) {
      icon = <FrownFilled />;
      key = 'bad';
    } else {
      // value === 0
      icon = <FrownFilled />;
      key = 'veryBad';
    }

    const color = colors.dataViz[key];

    return (
      <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
        {React.cloneElement(icon, { style: { color } })}
        <span className={styles.infoLabel}>{t(`symptomTracking.survey.generalState.${key}`)}</span>
      </div>
    );
  };

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'space-between',
        height: '100%',
        width: '100%',
      }}
    >
      <div>
        <div className={styles.infoRow}>
          {isPeriodTrackingEnabled && (
            <div className={styles.infoContainer}>
              <h4 className={styles.periodLabel}>{t('symptomTracking.graphs.menstruation')}</h4>
              <div className={styles.dropIconsContainer}>
                {Array.from({ length: 4 }, (_, index) => {
                  const dropNumber = index + 1;
                  const isActive = dayData !== undefined && dropNumber <= dayData.menstruation_amount;

                  return (
                    <Drop
                      key={index}
                      height={16}
                      width={16}
                      fill={isActive ? colors.dataViz.veryBad : colors.border.default}
                    />
                  );
                })}
              </div>
            </div>
          )}

          <div className={styles.infoContainer}>
            <h4 className={styles.periodLabel}>{t('symptomTracking.graphs.generalState')}</h4>
            {renderGeneralStateIcon()}
          </div>
        </div>

        <div className={styles.infoContainer}>
          {dayData !== undefined && dayData?.symptoms.length > 0 ? (
            <div className={styles.symptomsSection}>
              <h4 className={styles.periodLabel}>{t('symptomTracking.graphs.symptoms')}</h4>

              <div className={styles.symptomsContainer}>
                {dayData?.symptoms.map((symptom: string) => (
                  <SymptomTag key={symptom} symptom={symptom} />
                ))}
              </div>
              <div className={styles.symptomsFadeOverlay} />
            </div>
          ) : (
            <h4 className={styles.periodLabel}>{t('symptomTracking.noSymptoms')}</h4>
          )}
        </div>
      </div>

      {/* Clickable Icon Button */}
      <div
        data-testid={`tracking.${testIdLabel}.edit`}
        className={actionButtonStyles.actionButton}
        onClick={() => {
          onEditClick();
          posthog.capture(dayData !== undefined ? 'tracking.survey.edit_started' : 'tracking.survey.started', {
            source: posthogSource,
          });
        }}
      >
        {dayData !== undefined ? (
          <EditOutlined className={styles.editIcon} />
        ) : (
          <PlusOutlined className={styles.addIcon} />
        )}
      </div>
    </div>
  );
};

export default function HorizontalTrackingCards({
  displayTomorrow,
  onOpenCalendar,
}: {
  displayTomorrow: boolean;
  onOpenCalendar?: () => void;
}) {
  const styles = useStyles();
  const { t } = useTranslation();
  const [isSurveyModalOpen, setIsSurveyModalOpen] = useState(false);
  const [surveyDate, setSurveyDate] = useState(dayjs());
  const { hasTrackingForToday, getSpecificDailyStatus } = useSymptomTrackingData();
  const [isReminderModalOpen, setIsReminderModalOpen] = useState(false);

  // Get today's data
  const today = dayjs();
  const todayStatus = getSpecificDailyStatus(today);

  // Get yesterday's data
  const yesterday = dayjs().subtract(1, 'day');
  const yesterdayStatus = getSpecificDailyStatus(yesterday);
  const hasYesterdayTracking = yesterdayStatus !== undefined;

  // Older days, shown before "Yesterday", oldest first
  const pastDays = Array.from({ length: NUMBER_OF_ADDITIONAL_PAST_DAYS }, (_, index) =>
    dayjs().subtract(NUMBER_OF_ADDITIONAL_PAST_DAYS - index + 1, 'day'),
  );

  // Number of cards rendered before the "Today" card, used to center the scroll view on it
  const numberOfCardsBeforeToday = (onOpenCalendar ? 1 : 0) + pastDays.length + 1;

  const scrollContainerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const container = scrollContainerRef.current;
    if (container) {
      // Center on the Today card on mount
      const cardWidth = container.clientWidth * 0.7; // 70% of container width
      container.scrollLeft = cardWidth * numberOfCardsBeforeToday + 16;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className={styles.mainContainer}>
      {/* Fade overlay on the left */}
      <div className={styles.fadeOverlayLeft} />

      {/* Fade overlay on the right */}
      <div className={styles.fadeOverlayRight} />

      <div className={styles.scrollContainer} ref={scrollContainerRef}>
        {/* Calendar Card */}
        {onOpenCalendar && (
          <div className={styles.calendarCardWrapper}>
            <ModuleCard block={false}>
              <div
                className={styles.calendarCardContent}
                onClick={() => {
                  onOpenCalendar();
                  posthog.capture('tracking.calendar_opened_from_horizontal_cards');
                }}
                data-testid="tracking.calendar.open"
              >
                <CalendarFilled className={styles.calendarIcon} />
                <Button
                  variant="link"
                  style={{
                    margin: 0,
                    padding: 0,
                    border: 'none',
                    background: 'transparent',
                    color: colors.brand.accentStrong,
                    height: 'auto',
                    lineHeight: 'auto',
                  }}
                >
                  {t('symptomTracking.calendar.accessFullCalendar')}
                </Button>
              </div>
            </ModuleCard>
          </div>
        )}

        {/* Past days cards (before Yesterday) */}
        {pastDays.map(day => {
          const dayStatus = getSpecificDailyStatus(day);
          const hasDayTracking = dayStatus !== undefined;
          const dayKey = day.format('YYYY-MM-DD');

          return (
            <div key={dayKey} className={hasDayTracking ? styles.cardWrapperFilledOut : styles.cardWrapper}>
              <ModuleCard
                title={
                  <>
                    <span className={styles.dayCardTitle}>{day.format('dddd DD/MM')}</span>
                    {hasDayTracking && (
                      <CheckCircleFilled style={{ color: colors.feedback.success.solid, marginLeft: 8 }} />
                    )}
                  </>
                }
              >
                <div className={styles.cardContent}>
                  <TrackingDataDisplay
                    dayData={dayStatus}
                    onEditClick={() => {
                      setSurveyDate(day);
                      setIsSurveyModalOpen(true);
                    }}
                    posthogSource="horizontal_cards_history"
                    testIdLabel={dayKey}
                  />
                </div>
              </ModuleCard>
            </div>
          );
        })}

        {/* Yesterday Card */}
        <div className={hasYesterdayTracking ? styles.cardWrapperFilledOut : styles.cardWrapper}>
          <ModuleCard
            title={
              <>
                {t('common.yesterday')}
                {hasYesterdayTracking && (
                  <CheckCircleFilled style={{ color: colors.feedback.success.solid, marginLeft: 8 }} />
                )}
              </>
            }
          >
            <div className={styles.cardContent}>
              <TrackingDataDisplay
                dayData={yesterdayStatus}
                onEditClick={() => {
                  setSurveyDate(yesterday);
                  setIsSurveyModalOpen(true);
                }}
                posthogSource="horizontal_cards_yesterday"
                testIdLabel="yesterday"
              />
            </div>
          </ModuleCard>
        </div>

        {/* Today Card */}
        <div className={hasTrackingForToday ? styles.cardWrapperFilledOut : styles.cardWrapper}>
          <ModuleCard
            title={
              <>
                {t('common.today')}
                {hasTrackingForToday && (
                  <CheckCircleFilled style={{ color: colors.feedback.success.solid, marginLeft: 8 }} />
                )}
              </>
            }
          >
            <div className={styles.cardContent}>
              <TrackingDataDisplay
                dayData={todayStatus}
                onEditClick={() => {
                  setSurveyDate(today);
                  setIsSurveyModalOpen(true);
                }}
                posthogSource="horizontal_cards_today"
                testIdLabel="today"
              />
            </div>
          </ModuleCard>
        </div>

        {/* Tomorrow Card */}
        {displayTomorrow && (
          <div className={styles.cardWrapper}>
            <ModuleCard title={t('common.tomorrow')}>
              <div className={styles.cardContent}>
                <p className={styles.reminderSubtitle}>{t('symptomTracking.survey.reminder.explain')}</p>
                <Button
                  // `cardContent` clips overflow, and antd buttons default to
                  // `white-space: nowrap`, so this label was getting cut off
                  // rather than wrapping onto a second line. The extra
                  // vertical padding and line-height give the wrapped, two-line
                  // label room to breathe instead of the default single-line
                  // sizing cramming it in.
                  style={{
                    marginTop: 12,
                    marginBottom: 12,
                    whiteSpace: 'normal',
                    height: 'auto',
                    maxWidth: '100%',
                    paddingTop: 12,
                    paddingBottom: 12,
                    lineHeight: 1.3,
                  }}
                  icon={<BellFilled />}
                  onClick={() => {
                    // Navigate to reminder settings or trigger reminder setup
                    setIsReminderModalOpen(true);
                    posthog.capture('tracking.reminder.activated', {
                      source: 'horizontal_cards',
                    });
                  }}
                >
                  {t('symptomTracking.reminder.callToAction')}
                </Button>
              </div>
            </ModuleCard>
          </div>
        )}

        <SymptomTrackingSurveyModal
          key={surveyDate.format('YYYY-MM-DD')}
          open={isSurveyModalOpen}
          onClose={() => {
            setIsSurveyModalOpen(false);
          }}
          date={surveyDate}
        />
        <SymptomTrackingReminderModal
          hidePeriodReminder
          isOpen={isReminderModalOpen}
          onClose={() => setIsReminderModalOpen(false)}
        />
      </div>
    </div>
  );
}
